import { useState } from 'react';
import type { JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';

interface EmojiScaleFieldProps {
  palette: Palette;
  value: unknown;
  onChange: (value: number) => void;
  // Custom emoji set from question.settings.emojiIcons. Falls back to the
  // default 5-step set if not provided. Length capped at 5 to match main app.
  emojis?: string[];
}

const DEFAULT_EMOJI_SET = ['😠', '😕', '😐', '🙂', '😍'];
const PREMIUM_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

// Boxed emoji buttons. Hover lifts the border to a 60%-alpha accent and
// nudges the button up 1 px for tactility; selected commits to the full
// accent border + tinted background and stays flat so a committed answer
// doesn't continue to move.
export function EmojiScaleField({ palette, value, onChange, emojis }: EmojiScaleFieldProps): JSX.Element {
  const set = (emojis && emojis.length > 0 ? emojis : DEFAULT_EMOJI_SET).slice(0, 5);
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {set.map((emoji, index) => {
        const ratingValue = index + 1;
        const selected = value === ratingValue;
        return (
          <EmojiButton
            key={`${emoji}-${index}`}
            emoji={emoji}
            selected={selected}
            palette={palette}
            onClick={() => onChange(ratingValue)}
          />
        );
      })}
    </div>
  );
}

function EmojiButton({
  emoji,
  selected,
  palette,
  onClick,
}: {
  emoji: string;
  selected: boolean;
  palette: Palette;
  onClick: () => void;
}): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const borderColor = selected
    ? palette.primary
    : hovered
      ? hexWithAlpha(palette.primary, 0.6)
      : palette.border;
  const background = selected
    ? hexWithAlpha(palette.primary, 0.08)
    : palette.card;
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="chirp-btn"
      style={{
        flex: 1,
        fontSize: 24,
        padding: '8px 0',
        borderRadius: 10,
        border: `1px solid ${borderColor}`,
        background,
        cursor: 'pointer',
        transform: hovered && !selected ? 'translateY(-1px)' : 'translateY(0)',
        transition: `border-color 220ms ${PREMIUM_EASE}, background-color 220ms ${PREMIUM_EASE}, transform 220ms ${PREMIUM_EASE}`,
      }}
    >
      {emoji}
    </button>
  );
}
